import React from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Pencil, History } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { WikiOverlayProvider, useWikiOverlay } from '../contexts/WikiOverlayContext';
import { WikiShell } from '../components/wiki/WikiShell';
import { PageMetadataBar } from '../components/wiki/PageMetadataBar';
import { MarkdownRenderer } from '../components/wiki/MarkdownRenderer';
import { WikiEditorView, MyDraftsView, ReviewView, HistoryView } from '../components/wiki/WikiContribute';
import {
  getSearchablePages,
  getPageBySlugParts,
  getIndexPage,
} from '../lib/wiki/wikiData';
import { searchPages } from '../lib/wiki/search';

function ProposeEditButton({ slug, page, language }) {
  const { isAuthenticated, isDevMode } = useAuth();
  const navigate = useNavigate();
  if (!isAuthenticated && !isDevMode) return null;
  return (
    <button
      type="button"
      onClick={() => navigate('/wiki/drafts/new', {
        state: {
          kind: 'edit', slug, base_slug: slug, lang: language,
          title: page.title, summary: page.summary, content: page.content,
        },
      })}
      className="flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-sm font-medium text-gray-600 hover:bg-gray-50"
    >
      <Pencil className="h-3.5 w-3.5" />
      Propose edit
    </button>
  );
}

function WikiPageView({ slugParts }) {
  const { language, t } = useLanguage();
  const { overlay } = useWikiOverlay();
  const slug = slugParts.length ? slugParts.join('/') : 'index';

  const staticPage =
    slugParts.length === 0
      ? getIndexPage(language) ?? getPageBySlugParts([], language)
      : getPageBySlugParts(slugParts, language);

  const ov = overlay.bySlug[slug];

  if (!staticPage && !ov && slugParts.length > 0) {
    const overviewParts = [...slugParts, 'overview'];
    const overviewPage = getPageBySlugParts(overviewParts, language);
    if (overviewPage) {
      return <Navigate to={`/wiki/${overviewParts.join('/')}`} replace />;
    }
  }

  const page = ov
    ? {
        ...(staticPage || {}),
        title: ov.title || staticPage?.title || slug,
        summary: ov.summary ?? staticPage?.summary,
        content: ov.content,
        updated: ov.updated_at || staticPage?.updated,
      }
    : staticPage;

  if (!page) {
    return (
      <WikiShell>
        <div className="text-center py-16">
          <h1 className="text-xl font-semibold text-gray-900 mb-2">{t('wiki.notFoundTitle')}</h1>
          <p className="text-gray-500 mb-4">{t('wiki.notFoundDesc')}</p>
          <Link to="/wiki" className="text-blue-600 hover:underline">
            {t('wiki.backHome')}
          </Link>
        </div>
      </WikiShell>
    );
  }

  const lastEditor = ov ? (ov.author_email || ov.updated_by) : null;

  return (
    <WikiShell>
      <div className="mb-2 flex items-center justify-end gap-2">
        <Link
          to={`/wiki/history/${slug}`}
          className="flex items-center gap-1 rounded-md border border-gray-200 px-2.5 py-1 text-sm font-medium text-gray-600 hover:bg-gray-50"
        >
          <History className="h-3.5 w-3.5" />
          History
        </Link>
        <ProposeEditButton slug={slug} page={page} language={language} />
      </div>
      <PageMetadataBar
        title={page.title}
        summary={page.summary}
        updated={page.updated}
        owner={page.owner}
        reviewBy={page.reviewBy}
        status={page.status}
        localeFallback={page.localeFallback}
      />
      {lastEditor && (
        <p className="-mt-2 mb-4 text-xs text-gray-400">
          Last community edit by {lastEditor}
          {ov.updated_at ? ` · ${new Date(ov.updated_at).toLocaleDateString()}` : ''}
        </p>
      )}
      <MarkdownRenderer content={page.content} />
    </WikiShell>
  );
}

function WikiSearchView() {
  const { language, t } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const params = new URLSearchParams(location.search);
  const q = params.get('q') || '';
  const results = q ? searchPages(q, language) : [];
  const searchPagesList = getSearchablePages(language);

  const handleSubmit = (e) => {
    e.preventDefault();
    const form = e.target;
    const value = form.q.value.trim();
    navigate(value ? `/wiki/search?q=${encodeURIComponent(value)}` : '/wiki/search');
  };

  return (
    <WikiShell>
      <h1 className="text-2xl font-semibold text-gray-900 mb-2">{t('wiki.searchTitle')}</h1>
      <p className="text-sm text-gray-500 mb-6">
        {t('wiki.searchIntro', { count: searchPagesList.length })}
      </p>
      <form className="mb-8" onSubmit={handleSubmit}>
        <input
          name="q"
          key={q}
          defaultValue={q}
          placeholder={t('wiki.searchInputPlaceholder')}
          className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900"
        />
      </form>
      {q && (
        <ul className="space-y-4">
          {results.length === 0 ? (
            <li className="text-gray-500">{t('wiki.noResultsFor', { q })}</li>
          ) : (
            results.map((r) => (
              <li key={r.slug} className="border-b border-gray-100 pb-4">
                <Link
                  to={`/wiki/${r.slug}`}
                  className="text-blue-600 font-medium hover:underline"
                >
                  {r.title}
                </Link>
                <p className="text-sm text-gray-500 mt-1">{r.excerpt}</p>
              </li>
            ))
          )}
        </ul>
      )}
    </WikiShell>
  );
}

function WikiPageRouter() {
  const params = useParams();
  const rest = params['*'] || '';

  if (rest === 'eng-bootcamp' || rest.startsWith('eng-bootcamp/')) {
    const target =
      rest === 'eng-bootcamp'
        ? 'engineering-bootcamp/overview'
        : rest.replace(/^eng-bootcamp/, 'engineering-bootcamp');
    return <Navigate to={`/wiki/${target}`} replace />;
  }

  const slugParts = rest ? rest.split('/').filter(Boolean) : [];
  return <WikiPageView slugParts={slugParts} />;
}

export default function Wiki() {
  return (
    <WikiOverlayProvider>
      <Routes>
        <Route path="search" element={<WikiSearchView />} />
        <Route path="drafts" element={<WikiShell><MyDraftsView /></WikiShell>} />
        <Route path="drafts/new" element={<WikiShell><WikiEditorView /></WikiShell>} />
        <Route path="drafts/:id" element={<WikiShell><WikiEditorView /></WikiShell>} />
        <Route path="review" element={<WikiShell><ReviewView /></WikiShell>} />
        <Route path="history/*" element={<WikiShell><HistoryView /></WikiShell>} />
        <Route path="*" element={<WikiPageRouter />} />
      </Routes>
    </WikiOverlayProvider>
  );
}
